/**
 * Conjunto de ícones da interface.
 *
 * ## Por que traçado, e não emoji
 *
 * Emoji resolve rápido e é o que estava aqui. O problema é que ele é uma
 * FONTE: cada sistema desenha o seu, com peso, cor e estilo próprios. O mesmo
 * aviso aparecia com um traço fino no Windows, colorido e gordo no Mac, e
 * outro coisa no Android — dentro de um painel que o resto da plataforma
 * desenha com precisão.
 *
 * Traçado em SVG dá o mesmo desenho em todo lugar, acompanha a cor do texto
 * quando precisa, e escala sem borrar.
 *
 * ## As regras do conjunto
 *
 * Grade de 24, traço de 1,75, pontas e junções arredondadas, `currentColor`.
 * Todos os ícones seguem as quatro — é o que faz um conjunto parecer um
 * conjunto, e não uma coleção de desenhos avulsos.
 *
 * A exceção são as marcas de terceiro em `DestinationIcon.jsx`, que mantêm as
 * cores originais: recolorir logotipo alheio é outra conversa.
 */

/** As propriedades que todo ícone compartilha. */
const base = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
  focusable: 'false',
};

/** Aprovado. Círculo com a marca dentro, não a marca solta. */
function CheckCircle() {
  return (
    <svg {...base}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.25 12.4 11 15.1l4.9-5.6" />
    </svg>
  );
}

/** Recusado. */
function XCircle() {
  return (
    <svg {...base}>
      <circle cx="12" cy="12" r="9" />
      <path d="M14.9 9.1 9.1 14.9M9.1 9.1l5.8 5.8" />
    </svg>
  );
}

/** Enviado. Avião de papel, com a dobra que o faz ler como avião. */
function Send() {
  return (
    <svg {...base}>
      <path d="M21.4 2.6 10.6 13.4" />
      <path d="M21.4 2.6 14.5 21.4 10.6 13.4 2.6 9.5Z" />
    </svg>
  );
}

/** Acesso retirado. Cadeado fechado. */
function Lock() {
  return (
    <svg {...base}>
      <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" />
      <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" />
      <path d="M12 14.5v2" />
    </svg>
  );
}

/**
 * Pedido chegou para você — e **Solicitações** no menu do topo.
 *
 * Caixa de entrada com a bandeja. Mesma ideia nos dois lugares: o que chegou
 * e espera resposta.
 */
function Inbox() {
  return (
    <svg {...base}>
      <path d="M3.5 13.5h4l1.5 2.5h6l1.5-2.5h4" />
      <path d="M6.2 4.5h11.6a2 2 0 0 1 1.85 1.24l1.85 7.76v4.5a2 2 0 0 1-2 2H4.5a2 2 0 0 1-2-2v-4.5l1.85-7.76A2 2 0 0 1 6.2 4.5Z" />
    </svg>
  );
}

/**
 * Boas-vindas, e **Agentes** no menu do topo.
 *
 * Brilhos são o sinal de IA mais estabelecido que existe hoje, e a seção
 * Agentes é o catálogo de IA da casa. Reusar é melhor que desenhar um segundo
 * glifo parecido: dois desenhos próximos para a mesma ideia é o que faz um
 * conjunto deixar de parecer um conjunto.
 */
function Sparkles() {
  return (
    <svg {...base}>
      <path d="M11 3.5 12.6 8 17 9.6l-4.4 1.6L11 15.7 9.4 11.2 5 9.6 9.4 8Z" />
      <path d="M18 14.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8Z" />
    </svg>
  );
}

/** Item novo no portfólio. Caixa, que é o que se abre quando algo chega. */
function Package() {
  return (
    <svg {...base}>
      <path d="M20.5 8.2v7.6a1.6 1.6 0 0 1-.85 1.4l-6.85 3.7a1.6 1.6 0 0 1-1.6 0l-6.85-3.7a1.6 1.6 0 0 1-.85-1.4V8.2a1.6 1.6 0 0 1 .85-1.4l6.85-3.7a1.6 1.6 0 0 1 1.6 0l6.85 3.7a1.6 1.6 0 0 1 .85 1.4Z" />
      <path d="M3.6 7.4 12 12l8.4-4.6M12 12v8.9" />
    </svg>
  );
}

/**
 * Site do fornecedor.
 *
 * Substituiu a seta-saindo-da-caixa. A seta diz "abre em outro lugar", que é
 * o que TODOS os destinos fazem — não distinguia nada. O globo diz o que este
 * destino é: um site de fora, ao lado do Teams, do M365 e do Snowflake.
 */
function Globe() {
  return (
    <svg {...base}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3.3 9.5h17.4M3.3 14.5h17.4" />
      <path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18Z" />
    </svg>
  );
}

/**
 * Sistemas. Grade de aplicativos.
 *
 * Quatro quadrados é o sinal mais reconhecido para "conjunto de aplicações" —
 * e é exatamente o que a seção Sistemas é: ferramentas de terceiros, cada uma
 * com a sua porta. Um monitor ou uma engrenagem diriam "configuração", que é
 * outra coisa.
 */
function Grid() {
  return (
    <svg {...base}>
      <rect x="3.5" y="3.5" width="7" height="7" rx="1.6" />
      <rect x="13.5" y="3.5" width="7" height="7" rx="1.6" />
      <rect x="3.5" y="13.5" width="7" height="7" rx="1.6" />
      <rect x="13.5" y="13.5" width="7" height="7" rx="1.6" />
    </svg>
  );
}

/**
 * Artefatos. Janela de navegador.
 *
 * A barra no topo com os dois pontos é o que faz ler como *página publicada*,
 * que é literalmente o que um artefato é aqui: HTML autocontido com endereço
 * próprio. Um arquivo ou uma pasta diriam "documento guardado" — o artefato
 * não é guardado, é servido.
 *
 * Os pontos são traços de comprimento zero (`h.01`): com `strokeLinecap`
 * arredondado eles viram círculos, e dois `<circle>` de raio minúsculo
 * ficariam com peso diferente do resto do traçado.
 */
function Window() {
  return (
    <svg {...base}>
      <rect x="2.6" y="4.5" width="18.8" height="15" rx="2.2" />
      <path d="M2.6 9.4h18.8" />
      <path d="M5.9 7h.01M8.4 7h.01" />
    </svg>
  );
}

/** Uma pessoa. Usuários, no painel de administração. */
function User() {
  return (
    <svg {...base}>
      <circle cx="12" cy="8" r="3.75" />
      <path d="M4.5 20.2a7.5 7.5 0 0 1 15 0" />
    </svg>
  );
}

/**
 * Duas pessoas. Grupos.
 *
 * A segunda é um recorte da primeira, atrás e à direita — é o que faz ler como
 * "um conjunto de pessoas" sem precisar desenhar três cabeças num espaço de
 * dezoito pixels.
 */
function Users() {
  return (
    <svg {...base}>
      <circle cx="9.5" cy="8.5" r="3.25" />
      <path d="M3 19.8a6.6 6.6 0 0 1 13 0" />
      <path d="M16.2 5.6a3.25 3.25 0 0 1 0 5.9" />
      <path d="M17.6 14.1a6.6 6.6 0 0 1 3.4 5.7" />
    </svg>
  );
}

/**
 * Chave. Acessos concedidos.
 *
 * Não é o cadeado: o cadeado é o que está fechado, e esta aba é sobre o que
 * foi **aberto** para alguém. A diferença importa numa fileira onde os dois
 * poderiam aparecer.
 */
function Key() {
  return (
    <svg {...base}>
      <circle cx="7.8" cy="16.2" r="3.6" />
      <path d="M10.4 13.6 20 4" />
      <path d="M17.2 6.8l2.1 2.1" />
      <path d="M14.6 9.4l2.1 2.1" />
    </svg>
  );
}

/** Envelope. A caixa de dúvidas e sugestões. */
function Mail() {
  return (
    <svg {...base}>
      <rect x="2.6" y="5" width="18.8" height="14" rx="2.2" />
      <path d="M3.4 7 12 13 20.6 7" />
    </svg>
  );
}

/**
 * Balões de conversa. A telemetria do Copilot.
 *
 * Dois balões, e não um: o assunto é a troca entre pessoa e agente, não uma
 * mensagem solta — que é o que a aba ao lado (Mensagens) já representa.
 */
function Chat() {
  return (
    <svg {...base}>
      <path d="M3 8.2a2.6 2.6 0 0 1 2.6-2.6h7.6A2.6 2.6 0 0 1 15.8 8.2v3.4a2.6 2.6 0 0 1-2.6 2.6H7.4L3.8 17V8.2Z" />
      <path d="M18.2 9.4A2.6 2.6 0 0 1 21 12v3.4a2.6 2.6 0 0 1-2.6 2.6h-.9l-2.3 2.3v-2.3" />
    </svg>
  );
}

/**
 * Tour guiado. Bússola: quem está sendo levado por um caminho, não quem
 * procura informação — por isso bússola e não ponto de interrogação, que é o
 * sinal do "?" contextual e precisa continuar significando outra coisa.
 */
function Compass() {
  return (
    <svg {...base}>
      <circle cx="12" cy="12" r="8.6" />
      <path d="m15.2 8.8-1.9 4.5-4.5 1.9 1.9-4.5Z" />
    </svg>
  );
}

const SET = {
  'check-circle': CheckCircle,
  'x-circle': XCircle,
  send: Send,
  lock: Lock,
  inbox: Inbox,
  sparkles: Sparkles,
  package: Package,
  globe: Globe,
  grid: Grid,
  window: Window,
  user: User,
  users: Users,
  key: Key,
  mail: Mail,
  chat: Chat,
  compass: Compass,
};

/**
 * @param {object} p
 * @param {keyof SET} p.name
 * @param {string} [p.tone] classe de cor: `ok`, `danger`, `info`, `gold`, `muted`
 */
export function Icon({ name, tone = '' }) {
  const Glyph = SET[name];
  if (!Glyph) return null;
  return (
    <span className={`icon ${tone ? `icon-${tone}` : ''}`} aria-hidden="true">
      <Glyph />
    </span>
  );
}

export default Icon;
